// "We couldn't apply your deal" (decided 2026-10-04; built 2026-10-07 for
// Michael's look check). The deals client asks `confirmDealFallback` when it
// can't build the deal checkout (server down or slow, a Shopify error).
//   - No deal price in the basket: 'continue' at once (the $60 tin deal
//     counts only while the shopper's ad pass is live, deal-unlock.js) - normal checkout, as
//     before. Only a basket with a deal ever sees the page.
//   - A deal: the page (snippets/deal-fail-dialog.liquid) offers
//       Try again           once; after one failed retry it's gone
//       Get help            one quick step: "Email or phone" and an optional
//                           line; Send posts Shopify's own contact form (it
//                           emails teatime@, nothing stored by us) with the
//                           basket and deal added, then "Sent!". If it can't
//                           send: our email (prefilled) and phone instead.
//                           The post goes in the background through Shopify's
//                           own hCaptcha (see ownSubmit), never as a page post.
//       Pay full price      normal checkout; the cart is flagged
//                           _deals_refund_owed first, so the paid order says
//                           a refund is owed (deals' order check emails it);
//                           refunded within 2 business days
//     Escape or × closes it: the shopper stays, the basket untouched.
// A native modal <dialog>: the rest of the page is inert, Tab is kept inside,
// focus goes back to the Checkout button on close.
//
// Preview (dev theme only): ?deals_fail=1 makes every deal checkout fail
// (the client never asks the server, nothing is reported), ?deals_fail=0
// stops it. Only on a theme Shopify says is unpublished: the Liquid renders
// data-deal-fail-preview only there and this checks Shopify.theme.role too.

import { priceBasket } from '@deals/core/price'
import { fromAjaxCart, REFUND_ATTRIBUTE } from '@deals/core/basket'
import { formatMoney } from './product-price.js'
import { dealContext, unlockState, settled } from './deal-unlock.js'

const SUPPORT = 'teatime@highteawithharriet.com.au'
const PHONE = '1800 832 846'
const PHONE_HREF = 'tel:1800832846'
const PREVIEW_KEY = 'htwh:deals-fail'

const dialog = () => document.querySelector('[data-deal-fail]')

/** True only on an unpublished theme (the dev theme), never on live. */
function previewAllowed() {
  const role = window.Shopify?.theme?.role
  return Boolean(dialog()?.hasAttribute('data-deal-fail-preview')) && (role === 'unpublished' || role === 'development')
}

function readPreviewSwitch() {
  if (!previewAllowed()) return false
  try {
    const q = new URLSearchParams(location.search).get('deals_fail')
    if (q === '1') sessionStorage.setItem(PREVIEW_KEY, '1')
    if (q === '0') sessionStorage.removeItem(PREVIEW_KEY)
    return sessionStorage.getItem(PREVIEW_KEY) === '1'
  } catch {
    return false
  }
}
let forced = false

/** For the deals client: the preview makes every deal checkout fail. */
export const dealFailPreview = () => forced

const money = (cents) => formatMoney(cents / 100)

/** The basket and the deal, as lines of text for support. */
function basketLines(cart, priced) {
  const items = cart.items.filter((i) => i.quantity > 0)
  const lines = items.slice(0, 25).map((item) => {
    const p = priced.lines[cart.items.indexOf(item)]
    const full = money(p ? p.was * item.quantity : item.original_line_price ?? item.final_line_price)
    const deal = p && p.now < p.was ? ` (deal price ${money(p.now * item.quantity)})` : ''
    return `- ${item.title} x ${item.quantity}: ${full}${deal}`
  })
  if (items.length > 25) lines.push(`- and ${items.length - 25} more`)
  const deals = [...new Set(priced.lines.filter((p) => p.title && p.now < p.was).map((p) => p.title))]
  return [
    'Basket:',
    ...lines,
    '',
    `Deal: ${deals.join(', ')} (${money(priced.saving)} off)`,
    `Basket at full price: ${money(priced.was)}`,
    `With the deal: ${money(priced.now)}`,
  ]
}

/** If the send fails: an email to us with the basket already in it. */
function supportMail(cart, priced) {
  const subject = 'My deal didn’t apply at checkout'
  const body = ['Hi Harriet’s team,', '', 'My deal didn’t apply when I tried to check out. Could you help?', '', ...basketLines(cart, priced), '', 'Thanks,', ''].join('\n')
  return `mailto:${SUPPORT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** "Email or phone": an email address, or a phone number (8+ digits). */
function replyTo(value) {
  const v = value.trim()
  if (EMAIL.test(v)) return { email: v }
  if (/^[+()\d\s-]+$/.test(v) && v.replace(/\D/g, '').length >= 8) return { phone: v }
  return null
}

/**
 * Fills Shopify's contact form with the help request: the reply address, their
 * line, the basket, the deal and what went wrong.
 */
function fillHelp(form, { reply, note, cart, priced, reason, preview }) {
  const set = (name, value) => (form.querySelector(`[name="${name}"]`).value = value)
  // Shopify's form needs an email: a phone-only request goes from our own
  // address, with the phone in the message.
  set('contact[email]', reply.email ?? SUPPORT)
  set('contact[name]', preview ? 'TEST deal help (dev preview)' : 'Deal help (cart)')
  set(
    'contact[body]',
    [
      ...(preview ? ['TEST from the dev preview - please ignore.', ''] : []),
      'A shopper’s deal didn’t apply at checkout; they asked for help from the cart.',
      `Reply to: ${reply.email ?? reply.phone}${reply.phone ? ' (phone)' : ''}`,
      `Their note: ${note || '-'}`,
      '',
      ...basketLines(cart, priced),
      `What went wrong: ${reason}`,
    ].join('\n')
  )
}

/**
 * POST /contact in the background. Shopify redirects a message it took to
 * ?contact_posted=true; anything else (its form errors, a bot check, no
 * connection) counts as not sent.
 */
async function postContact(form) {
  try {
    const res = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
      signal: AbortSignal.timeout?.(10000),
    })
    return res.ok && /[?&]contact_posted=true/.test(res.url)
  } catch {
    return false
  }
}

// Shopify's contact-form spam protection (its captcha-bootstrap script, then
// storefront-forms-hcaptcha) binds invisible hCaptcha to the form when it's
// first focused. On submit it gets a token, adds h-captcha-response and calls
// form.submit() - a full page post, which reloaded the page to
// ?contact_posted=true#deal-fail-help and lost the popup (Michael's test,
// 2026-10-08). The binder calls the form's OWN submit if it has one, so ours,
// set before it binds, receives the token-carrying form and posts it in the
// background instead. Nothing ever does the native page post.
let posting = null // settles the send under way; null when none is
function ownSubmit(form) {
  if (form.dataset.dealFailSubmit) return
  form.dataset.dealFailSubmit = '1'
  form.submit = () => {
    if (posting) posting(postContact(form))
  }
}

/** Waits (briefly) for Shopify's hCaptcha to finish binding, if it started. */
async function captchaSettled(form, ms = 6000) {
  const bound = () => form.hasAttribute('data-hcaptcha-bound') || form.dataset.recaptchaBound
  // data-cptcha: Shopify's bootstrap has asked for the captcha on this form.
  for (let waited = 0; form.dataset.cptcha && !bound() && waited < ms; waited += 100) {
    await new Promise((r) => setTimeout(r, 100))
  }
}

/**
 * Sends the filled form: through Shopify's hCaptcha when it's on the form
 * (its token first, then our background post), otherwise straight away.
 * Capped: a captcha that never answers (e.g. a picture puzzle, which the
 * modal page would keep out of reach) counts as not sent.
 */
function sendHelp(form, ms = 20000) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => settle(false), ms)
    function settle(result) {
      if (posting !== settle) return
      posting = null
      clearTimeout(timer)
      Promise.resolve(result).then(resolve, () => resolve(false))
    }
    posting = settle
    captchaSettled(form).then(() => form.submit())
  })
}

/** Flags the cart so the full-price order shows the refund owed (capped: never holds the shopper long). */
async function flagRefund(priced, preview) {
  const when = new Date().toLocaleString('en-AU', {
    timeZone: 'Australia/Perth',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
  const deals = [...new Set(priced.lines.filter((p) => p.title && p.now < p.was).map((p) => p.title))].join(', ')
  const value = preview
    ? 'TEST from the dev preview - no refund owed'
    : `Refund ${money(priced.saving)} within 2 business days: deal (${deals}) couldn't be applied; shopper chose full price ${when} Perth`
  flagged = true
  await Promise.race([
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [REFUND_ATTRIBUTE]: value } }),
    }).catch(() => {}),
    new Promise((r) => setTimeout(r, 1500)),
  ])
}

// The flag lives only while the shopper is at checkout: back on the site, it
// comes off, so a later checkout (with its deal) isn't flagged by mistake.
let flagged = document.documentElement.hasAttribute('data-deals-refund-flag')
function clearFlag() {
  if (!flagged) return
  flagged = false
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [REFUND_ATTRIBUTE]: '' } }),
  }).catch(() => {})
}

let open = null // { resolve, returnTo } while the page is showing
let busy = false

function setBusy(el, label) {
  const d = dialog()
  busy = Boolean(el)
  d.querySelectorAll('[data-deal-fail-retry], [data-deal-fail-full], [data-deal-fail-help], [data-deal-fail-send], [data-deal-fail-close]').forEach((b) => {
    if (busy) b.setAttribute('aria-disabled', 'true')
    else b.removeAttribute('aria-disabled')
    b.classList.toggle('pointer-events-none', busy)
    b.classList.toggle('opacity-60', busy && b !== el)
  })
  if (el) {
    el.dataset.label ??= el.textContent
    el.setAttribute('aria-busy', 'true')
    el.textContent = label
    const status = d.querySelector('[data-deal-fail-status]')
    if (status) status.textContent = label
  }
}

function finish(choice) {
  if (!open) return
  const { resolve } = open
  if (choice === 'stay') {
    const { returnTo } = open
    open = null
    dialog().close()
    returnTo?.focus?.()
  }
  resolve(choice)
}

function wire(d) {
  if (d.dataset.wired) return
  d.dataset.wired = '1'
  d.querySelector('[data-deal-fail-retry]').addEventListener('click', (e) => {
    if (busy) return
    setBusy(e.currentTarget, d.dataset.retrying)
    finish('retry')
  })
  d.querySelector('[data-deal-fail-full]').addEventListener('click', async (e) => {
    if (busy || !open) return
    setBusy(e.currentTarget, d.dataset.leaving)
    await flagRefund(open.priced, open.preview)
    finish('full-price')
  })
  // Get help: one quick step in the page, then Send.
  const step = d.querySelector('[data-deal-fail-step]')
  const form = step.querySelector('form')
  form.noValidate = true // our own messages, read out by screen readers
  const replyInput = form.querySelector('[data-deal-fail-reply]')
  const error = form.querySelector('[data-deal-fail-error]')
  d.querySelector('[data-deal-fail-help]').addEventListener('click', (e) => {
    if (busy) return
    e.currentTarget.hidden = true
    e.currentTarget.setAttribute('aria-expanded', 'true')
    step.hidden = false
    replyInput.focus()
  })
  ownSubmit(form)
  form.addEventListener('submit', async (e) => {
    // Ours alone: Shopify's hCaptcha listeners (on the form and the document)
    // would otherwise post the whole page. sendHelp runs the captcha itself.
    e.preventDefault()
    e.stopImmediatePropagation()
    if (busy || !open) return
    const reply = replyTo(replyInput.value)
    error.replaceChildren()
    replyInput.removeAttribute('aria-invalid')
    if (!reply) {
      error.textContent = d.dataset.replyMissing
      replyInput.setAttribute('aria-invalid', 'true')
      replyInput.focus()
      return
    }
    const send = form.querySelector('[data-deal-fail-send]')
    setBusy(send, d.dataset.sending)
    fillHelp(form, {
      reply,
      note: form.querySelector('[data-deal-fail-note]').value.trim().slice(0, 300),
      ...open,
    })
    const sent = await sendHelp(form)
    send.removeAttribute('aria-busy')
    send.textContent = send.dataset.label
    setBusy(null)
    if (sent) {
      showSent(d)
    } else {
      // Not sent: our email (with the basket filled in) and phone instead.
      const mail = Object.assign(document.createElement('a'), { href: supportMail(open.cart, open.priced), textContent: SUPPORT, className: 'underline' })
      const tel = Object.assign(document.createElement('a'), { href: PHONE_HREF, textContent: PHONE, className: 'underline whitespace-nowrap' })
      const [before, middle, after] = d.dataset.sendFailed.split(/\{email\}|\{phone\}/)
      error.replaceChildren(before, mail, middle, tel, after)
    }
  })
  d.querySelector('[data-deal-fail-close]').addEventListener('click', () => !busy && finish('stay'))
  // Escape: closes the page and keeps the shopper on the site - unless a
  // retry or the move to checkout is under way.
  d.addEventListener('cancel', (e) => {
    e.preventDefault()
    if (!busy) finish('stay')
  })
  // Keep Tab inside the page (the rest is inert already; this also skips the
  // browser's own toolbar).
  d.addEventListener('keydown', (e) => {
    // Escape is the page's own (its cancel event): kept from cardo's drawer,
    // which would otherwise close the slide-out cart behind it too.
    if (e.key === 'Escape') e.stopPropagation()
    if (e.key !== 'Tab') return
    const items = [...d.querySelectorAll('button, a[href]')].filter((x) => !x.hidden && x.offsetParent !== null)
    if (!items.length) return
    const first = items[0]
    const last = items.at(-1)
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  })
}

/** "Sent! We'll be in touch soon." in place of the Get help step. */
function showSent(d) {
  d.querySelector('[data-deal-fail-help]').hidden = true
  d.querySelector('[data-deal-fail-step]').hidden = true
  const done = d.querySelector('[data-deal-fail-sent]')
  done.hidden = false
  done.focus()
}

/** A fresh page: Get help back to its button, nothing typed, nothing sent. */
function resetHelp(d) {
  const help = d.querySelector('[data-deal-fail-help]')
  help.hidden = false
  help.setAttribute('aria-expanded', 'false')
  d.querySelector('[data-deal-fail-step]').hidden = true
  d.querySelector('[data-deal-fail-sent]').hidden = true
  d.querySelector('[data-deal-fail-error]').replaceChildren()
  d.querySelectorAll('[data-deal-fail-reply], [data-deal-fail-note]').forEach((x) => {
    x.value = ''
    x.removeAttribute('aria-invalid')
  })
}

/** Draw the page for this failure; the retry button only on the first. */
function draw(d, priced, attempt, canRetry = attempt < 2) {
  const saving = money(priced.saving)
  d.querySelector('[data-deal-fail-text]').textContent = (attempt < 2 ? d.dataset.textFirst : d.dataset.textAgain).replaceAll('{saving}', saving)
  const retry = d.querySelector('[data-deal-fail-retry]')
  retry.hidden = !canRetry
  retry.removeAttribute('aria-busy')
  if (retry.dataset.label) retry.textContent = retry.dataset.label
  const full = d.querySelector('[data-deal-fail-full]')
  full.removeAttribute('aria-busy')
  full.textContent = d.dataset.fullLabel.replaceAll('{saving}', saving)
  // The new look lands at once: the buttons' own 0.35 s fade would show them
  // faded, and black blending to gold through olive (Michael, 2026-10-09).
  const buttons = [...d.querySelectorAll('button')]
  buttons.forEach((b) => (b.style.transition = 'none'))
  // With Try again gone, paying full price becomes the main button.
  full.classList.toggle('btn-primary', !canRetry)
  full.classList.toggle('btn-secondary', canRetry)
  setBusy(null)
  void d.offsetWidth // apply the new look before the fade comes back
  buttons.forEach((b) => (b.style.transition = ''))
  return canRetry ? retry : full
}

/**
 * The deals client's confirmFallback: 'continue' for a basket without a deal
 * (normal checkout, as before), otherwise what the shopper chose.
 */
export function confirmDealFallback({ reason, attempt, cart, preview, context = dealContext() }) {
  const d = dialog()
  if (!d || !cart?.items?.length) return 'continue'
  const priced = priceBasket(fromAjaxCart(cart), undefined, context)
  if (!(priced.saving > 0)) return 'continue'
  wire(d)
  if (!d.open) resetHelp(d)
  const focusFirst = draw(d, priced, attempt)
  return new Promise((resolve) => {
    const returnTo = open?.returnTo ?? document.activeElement
    open = { resolve, returnTo, priced, preview, cart, reason }
    if (!d.open) d.showModal()
    focusFirst.focus()
    if (attempt > 1) {
      // Screen readers hear that the retry didn't work.
      const status = d.querySelector('[data-deal-fail-status]')
      if (status) status.textContent = d.querySelector('[data-deal-fail-text]').textContent
    }
  })
}

// An ad tap whose pass never came (deal-unlock.js 'failed': the request
// failed, timed out or was refused). The server would give a plain checkout
// and the client would go to full price without a word, so Checkout asks
// here first, pricing the basket as the ad promised: Try again reloads the
// page (asking for the pass again), Pay full price flags the refund. Still
// being checked: Checkout waits for the answer. Registered before the deals
// client's own capture listener on window, so this runs first (review
// 2026-10-10).
const CHECKOUT = 'button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
let passing = false
if (document.documentElement.dataset.dealsApi) {
  addEventListener(
    'click',
    async (e) => {
      const button = e.target.closest?.(CHECKOUT)
      if (!button || passing || !['pending', 'failed'].includes(unlockState())) return
      e.preventDefault()
      e.stopImmediatePropagation()
      const go = () => {
        passing = true
        button.click()
        passing = false
      }
      if ((await settled()) !== 'failed') return go()
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null)
      const choice = await confirmDealFallback({
        reason: 'ad pass never came',
        attempt: 1,
        cart,
        preview: false,
        context: { unlocks: ['meta-ad'] },
      })
      if (choice === 'continue') return go()
      if (choice === 'retry') return location.reload()
      if (choice !== 'full-price') return
      await Promise.race([Promise.resolve(window.pulse?.syncCart?.()).catch(() => {}), new Promise((r) => setTimeout(r, 1500))])
      location.assign('/checkout')
    },
    true
  )
}

/** Called once at start: reads the preview switch, clears an old flag. */
export function startDealFail() {
  forced = readPreviewSwitch()
  const badge = document.querySelector('[data-deal-fail-badge]')
  if (badge) badge.hidden = !forced
  clearFlag()
  const form = dialog()?.querySelector('[data-deal-fail-step] form')
  if (form) ownSubmit(form)
  sentOnReturn()
  // Back from checkout (back/forward cache): the flag comes off, the page closes.
  addEventListener('pageshow', (e) => {
    if (!e.persisted) return
    clearFlag()
    const d = dialog()
    if (d?.open) {
      open = null
      d.close()
    }
    setBusy(null)
  })
}

/**
 * Safety net: if a help request ever goes as a full page post after all
 * (Shopify changes its captcha script), Shopify sends the shopper back to
 * the page with ?contact_posted=true#deal-fail-help. The page then opens
 * straight on "Sent!" for the basket's deal, and the address is tidied.
 * Try again isn't offered (no checkout is waiting on it); Pay full price
 * flags the cart and goes to Shopify's normal checkout itself.
 */
function sentOnReturn() {
  const d = dialog()
  const q = new URLSearchParams(location.search)
  if (!d || q.get('contact_posted') !== 'true' || location.hash !== '#deal-fail-help') return
  q.delete('contact_posted')
  try {
    history.replaceState(history.state, '', location.pathname + (q.size ? `?${q}` : ''))
  } catch {}
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      if (!cart?.items?.length) return
      const priced = priceBasket(fromAjaxCart(cart), undefined, dealContext())
      if (!(priced.saving > 0) || d.open) return
      wire(d)
      resetHelp(d)
      draw(d, priced, 1, false)
      const resolve = async (choice) => {
        if (choice !== 'full-price') return
        // Pulse's ad details go on the cart first, as with the deals client.
        await Promise.race([Promise.resolve(window.pulse?.syncCart?.()).catch(() => {}), new Promise((r) => setTimeout(r, 1500))])
        location.assign('/checkout')
      }
      open = { resolve, returnTo: document.activeElement, priced, preview: forced, cart, reason: 'sent from the page after a reload' }
      d.showModal()
      showSent(d)
    })
    .catch(() => {})
}
